import { useEffect, useState } from "react";

import styled from "styled-components";

import { EmptyImage } from "shared/ui/media/EmptyImage";
import { isEmptyImageSrc } from "shared/ui/media/imageFallback";

const toSize = (size) => (typeof size === "number" ? `${size}px` : size);

const Image = styled.img`
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  border-radius: ${({ $radius }) => $radius};
  border: ${({ $bordered }) =>
    $bordered ? "1px solid var(--admin-gray-stroke, #e8e8e8)" : "none"};
  object-fit: cover;
  flex-shrink: 0;
`;

/**
 * 표·목록에서 쓰는 공용 썸네일.
 * 이미지가 없거나(빈 값/기본 이미지) 로드에 실패하면 깨진 아이콘 대신
 * EmptyImage("이미지 없음" 자리표시)를 보여준다.
 * 이미지를 표시하는 모든 표에서 동일하게 재사용한다.
 *
 * @param src      이미지 URL
 * @param alt      대체 텍스트 (기본 "")
 * @param size     한 변 크기. number(px) 또는 CSS 문자열 (기본 50)
 * @param radius   border-radius (기본 4px)
 * @param bordered 테두리 표시 여부 (기본 true). 이미 프레임이 있는 곳은 false.
 */
export function Thumbnail({
  src,
  alt = "",
  size = 50,
  radius = "4px",
  bordered = true,
  className,
  style,
  ...rest
}) {
  const [failed, setFailed] = useState(false);

  // src가 바뀌면 실패 상태를 초기화한다(목록 재정렬/재조회 대비).
  useEffect(() => {
    setFailed(false);
  }, [src]);

  const showPlaceholder = isEmptyImageSrc(src) || failed;

  if (showPlaceholder) {
    return (
      <EmptyImage
        size={size}
        radius={radius}
        bordered={bordered}
        alt={alt || "이미지 없음"}
        className={className}
        style={style}
      />
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      $size={toSize(size)}
      $radius={radius}
      $bordered={bordered}
      className={className}
      style={style}
      onError={() => setFailed(true)}
      {...rest}
    />
  );
}

export default Thumbnail;
